import { createAuth0Client, Auth0Client } from "@auth0/auth0-spa-js";
import { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import {
  ALL_ORGS,
  ORG_HEADER,
  PRODUCT,
  activeOrgHeader,
} from "../utils/activeOrg";
import { isOrgRefusal, reportOrgRefusal } from "../utils/orgRefusal";

export const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || "",
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || "",
  authorizationParams: {
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    redirect_uri: window.location.origin,
  },
  cacheLocation: "localstorage" as const,
  // Renew through the refresh token grant rather than a hidden iframe. The
  // iframe carries a third-party cookie to the Auth0 domain, which browsers
  // increasingly refuse, and that is what left renewal failing for anyone whose
  // token had gone cold. The SDK adds `offline_access` to the scope itself when
  // this is on, so the scope above stays as it is.
  //
  // Requires "Allow Offline Access" on the API and the refresh_token grant on
  // the application. Without them Auth0 omits the refresh token silently and
  // every renewal fails.
  useRefreshTokens: true,
  // The SDK's own default, set explicitly because it is a decision rather than
  // an oversight. True would fall back to the iframe when the refresh grant
  // fails, which is the thing being escaped; false lets the failure reach
  // getAccessToken, which signs the user back in instead.
  useRefreshTokensFallback: false,
  // Matches the refresh token's 30-day absolute expiration, and is not
  // cosmetic: checkSession() returns early and never reaches the refresh token
  // at all once the `auth0.is.authenticated` cookie has gone, and that cookie is
  // written with `daysUntilExpire: sessionCheckExpiryDays`. Left at the SDK
  // default of 1, a user coming back after a day would be sent to log in again
  // with a perfectly good refresh token sitting unused beside them.
  sessionCheckExpiryDays: 30,
  onRedirectCallback: (appState?: any) => {
    window.history.replaceState(
      {},
      document.title,
      appState?.returnTo || window.location.pathname,
    );
  },
};

/**
 * Memoised on the PROMISE rather than on the resolved client.
 *
 * Home opens half a dozen queries in the same tick. Guarding on the resolved
 * client means every one of them looks while the first is still awaiting, finds
 * nothing, and builds an Auth0Client of its own - each running its own
 * checkSession, each reaching for the same cross-instance silent-auth lock. One
 * client, one silent auth, whatever the burst size.
 */
let clientPromise: Promise<Auth0Client> | undefined;

const getAuthClient = () => {
  if (!clientPromise) {
    clientPromise = createAuth0Client(auth0Config).catch((err) => {
      // A failed bootstrap must not be the cached answer forever.
      clientPromise = undefined;
      throw err;
    });
  }
  return clientPromise;
};

/**
 * Marks an error as "the session has gone and we could not get a token", as
 * distinct from "the API answered with an error" or "the token request itself
 * failed".
 *
 * They are not the same thing to the user. A data error or a dropped connection
 * deserves the endpoint's own message; a session that has gone deserves a
 * session prompt. Reporting the second
 * as the first is what puts a row of "Failed to list devices" on screen while the
 * app is only still settling its auth.
 */
export const AUTH_ERROR_STATUS = "AUTH_REQUIRED";

export const isAuthError = (error: unknown): boolean =>
  !!error &&
  typeof error === "object" &&
  (error as { status?: unknown }).status === AUTH_ERROR_STATUS;

/**
 * A token, with one retry.
 *
 * The first load after a spell away starts from an expired access token, so the
 * whole opening burst of queries reaches for a renewal at once and only one of
 * them performs it. The rest can lose that race, and the retry is a read of a
 * cache the winner has by then filled.
 *
 * Rotation makes the retry worth more rather than less. Each refresh token is
 * single-use, so a renewal in another tab or on another device can rotate ours
 * away between our read and our POST; that comes back `invalid_grant`, and by
 * the second attempt the winner's replacement is in the shared cache. Two
 * attempts is the ceiling however many queries are waiting - auth0-spa-js
 * collapses concurrent getTokenSilently calls for the same audience onto one
 * promise, and serialises across tabs behind its own lock.
 */
const getToken = async (auth0: Auth0Client) => {
  try {
    return await auth0.getTokenSilently();
  } catch (first) {
    return await auth0.getTokenSilently();
  }
};

const authError = (message: string) =>
  Object.assign(new Error(message), { status: AUTH_ERROR_STATUS });

// The `error` codes auth0-spa-js sets when the session itself cannot be renewed
// and only signing in again will help. Anything else - a failed fetch to
// /oauth/token, the SDK's lock timeout, a request timeout - says nothing about
// the session, and sending the user to Auth0 for it would navigate an offline
// user to a page that cannot load.
const SESSION_ERROR_CODES = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
]);

const isSessionError = (err: unknown): boolean =>
  SESSION_ERROR_CODES.has((err as { error?: unknown })?.error as string);

const tokenError = (err: unknown) => {
  const message = (err as Error)?.message || "Could not authenticate";
  return isSessionError(err) ? authError(message) : new Error(message);
};

// Records that we have already sent the user to Auth0 to sign in again. Kept in
// sessionStorage rather than a module variable because the redirect is a full
// page navigation: anything held in memory is gone by the time we come back and
// could tell us we have already tried. Per-tab, so one tab looping does not stop
// another from recovering.
const REAUTH_MARK_KEY = `vectech.${PRODUCT}.reauthAt`;

// Coming straight back from Auth0 and still being unable to mint a token means
// that redirect fixed nothing, and going round again would leave the user on a
// page they cannot get off. Past this window a failure is a genuinely new
// expiry rather than the same one repeating.
const REAUTH_RETRY_WINDOW_MS = 60_000;

// sessionStorage throws outright in some privacy modes rather than returning
// null, so every access is guarded, as activeOrg guards localStorage. Losing the
// mark costs the loop guard, not correctness.
const readReauthMark = (): number => {
  try {
    return Number(window.sessionStorage.getItem(REAUTH_MARK_KEY)) || 0;
  } catch (e) {
    return 0;
  }
};

const writeReauthMark = (at: number | null): void => {
  try {
    if (at === null) window.sessionStorage.removeItem(REAUTH_MARK_KEY);
    else window.sessionStorage.setItem(REAUTH_MARK_KEY, String(at));
  } catch (e) {
    // Ignored - see readReauthMark.
  }
};

// True from the moment a sign-in redirect is started until the page actually
// goes away. Navigation is not instant, and the queries that lost their token
// resolve in the meantime; this is how they know to stay quiet.
let reauthenticating = false;

/** Whether a sign-in redirect is under way, and the page therefore leaving. */
export const isReauthenticating = (): boolean => reauthenticating;

/**
 * Send the user through Auth0 for a fresh session, returning them to where they
 * are now.
 *
 * This is what an expired session should do. The alternative - telling someone
 * their session expired and to reload - is the same journey with a manual step
 * bolted to the front of it, and they have nothing else to do on that page
 * anyway.
 *
 * It declines, leaving the caller to report the failure instead, when we have
 * just come back from Auth0 and still cannot mint a token. That is the one case
 * where redirecting again is not a recovery but an unescapable loop.
 */
const startReauthentication = async (auth0: Auth0Client): Promise<void> => {
  if (reauthenticating) return;

  const lastAttempt = readReauthMark();
  if (lastAttempt && Date.now() - lastAttempt < REAUTH_RETRY_WINDOW_MS) return;

  reauthenticating = true;
  writeReauthMark(Date.now());
  try {
    await auth0.loginWithRedirect({
      appState: {
        // Matches AuthLayout, and auth0Config's onRedirectCallback is what puts
        // the user back on it.
        returnTo: window.location.pathname + window.location.search,
      },
    });
  } catch (err) {
    // The navigation did not happen, so the page is staying and the caller's
    // message is the only thing the user will get.
    reauthenticating = false;
  }
};

/**
 * The access token, or a rejection. `isAuthError` recognises the rejection when
 * the session has gone; a failure that is not about the session, such as the
 * network, rejects with a plain error so it is reported as what it is.
 *
 * Scout's two request layers both come through here - axiosBaseQuery below and
 * WebServerProvider's sendRequest - so the opening burst collapses onto one
 * client and one silent auth across the whole app, and neither layer has to
 * invent its own idea of what a token failure looks like.
 *
 * A session that has gone also starts the sign-in redirect from here, for the
 * same reason: one choke point, so a burst of failures produces one trip to
 * Auth0.
 */
export const getAccessToken = async (): Promise<string> => {
  let auth0: Auth0Client;
  try {
    auth0 = await getAuthClient();
  } catch (err) {
    throw tokenError(err);
  }

  try {
    const token = await getToken(auth0);
    // A working token closes the loop guard's window: whatever fails next is a
    // new expiry rather than this one repeating.
    if (readReauthMark()) writeReauthMark(null);
    return token;
  } catch (err) {
    if (isSessionError(err)) await startReauthentication(auth0);
    throw tokenError(err);
  }
};

interface CustomArgs extends AxiosRequestConfig {
  devPort?: string;
  // This allows us to pass the token from the component call if needed,
  baseUrl?: string;
  /**
   * Act as this organization for THIS request only, instead of the one the user
   * currently has selected.
   *
   * For the superuser panel, which shows another organization's data without
   * moving the operator's own context. The backend already accepts the
   * organization per request and already permits a superuser to name any one, so
   * nothing server-side changes.
   *
   * Belongs in the query ARG rather than being read from a global, so it lands
   * in RTK Query's cache key: two organizations viewed in one session must not
   * share a cache entry, or the panel poisons the operator's own data.
   *
   * `ALL_ORGS` is the exception: it acts as the operator's own organization
   * and widens the scope instead (see its doc comment in utils/activeOrg).
   */
  org?: string;
}

const getBaseURL = (devPort?: string) => {
  const protocol = process.env.REACT_APP_ENV !== "dev" ? "https://" : "http://";
  const endpoint = process.env.REACT_APP_BACKEND_ENDPOINT || "";
  const base = protocol + endpoint;

  if (devPort && process.env.REACT_APP_ENV === "dev") {
    return base.slice(0, -4) + devPort;
  }
  return base;
};

export const axiosBaseQuery =
  (): BaseQueryFn<CustomArgs, unknown, unknown> =>
  async ({ url, method, data, params, headers, devPort, org }) => {
    // Acquired in its own try, so a token we could not get is never dressed up as
    // this endpoint having failed.
    let token: string;
    try {
      token = await getAccessToken();
    } catch (err) {
      // A failure that is not about the session takes the same shape as a
      // request that never reached the server, so it gets the endpoint's own
      // message rather than a session one.
      return {
        error: {
          status: isAuthError(err) ? AUTH_ERROR_STATUS : undefined,
          data: (err as Error).message,
        },
      };
    }

    // ALL_ORGS is a scope, not an organization: it travels as a parameter and
    // the request still acts as the operator's own selection.
    const allOrgs = org === ALL_ORGS;

    try {
      const result = await axios({
        url: getBaseURL(devPort) + url,
        method,
        data,
        params: allOrgs ? { ...params, all_orgs: true } : params,
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          // An explicit org wins for this one request; otherwise the user's own
          // selection, read at call time so switching takes effect on the next
          // request. Omitted entirely when neither is set, which is how this
          // stays a no-op for single-org users.
          ...(org && !allOrgs ? { [ORG_HEADER]: org } : activeOrgHeader()),
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      // Only for the user's own organization. A request naming one explicitly is
      // the superuser panel looking at another tenant, and that tenant being
      // refused (deactivated, say) says nothing about the operator. ALL_ORGS
      // still acts as the operator's own, so it is theirs to report.
      if (
        (!org || allOrgs) &&
        isOrgRefusal(err.response?.status, err.response?.data)
      ) {
        reportOrgRefusal();
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
